import { useEffect, useState } from "react";
import { Dialog, DialogContent } from "@/view/components/ui/dialog"
import { Button } from "@/view/components/ui/button";
import { IAuthorizationStep } from "./authorizationContent";
import { useSelfHiringContext } from "@/app/contexts/SelfHiringContext";
import { Spinner } from "@/view/components/ui/spinner";
import { PartnerType } from "@/app/utils/partners.enum";
import { Whatsapp } from "iconsax-react";
import { useToast } from "@/view/components/ui/use-toast";

interface AuthorizationStepsModalProps {
  steps: IAuthorizationStep['steps'];
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  verifyAuthorization: (type: PartnerType.FGTS | PartnerType.PAN) => Promise<boolean>;
}

export const AuthorizationStepsModal = ({ steps, isOpen, onOpenChange, verifyAuthorization }: AuthorizationStepsModalProps) => {

  const { toast } = useToast()
  const { type } = useSelfHiringContext()
  const [currentStep, setCurrentStep] = useState(0)
  const step = steps?.[currentStep]
  const stepLink = !!step?.link
  const [isImageLoading, setIsImageLoading] = useState(true)
  const [hasImageError, setHasImageError] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    if (!isOpen || !steps?.length) return;

    steps.forEach(({ image }) => {
      if (!image) return;
      const img = new Image();
      img.src = image;
    });
  }, [isOpen, steps]);

  useEffect(() => {
    if (!isOpen) return;
    setHasImageError(false)
    setIsImageLoading(Boolean(step?.image))
  }, [isOpen, currentStep, step?.image]);

  const formatDescription = (text: string) => {
    const parts = text.split(/"([^"]*)"/);
    return parts.map((part, index) => {
      if (index % 2 === 1) {
        return (
          <span key={index} className="font-semibold">
            {`"${part}"`}
          </span>
        );
      }
      return part;
    });
  };
  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep((s) => Math.max(0, s - 1))
    } else onOpenChange(false)
  }

  const handleClose = () => {
    onOpenChange(false)
    setCurrentStep(0)
  }

  const handleNext = async () => {
    if (currentStep < (steps?.length ?? 0) - 1) {
      setCurrentStep((s) => s + 1)
    } else {
      if (type === PartnerType.PAN || type === PartnerType.FGTS) {
        try{
          setIsLoading(true)
          const isAuthorized = await verifyAuthorization(type);
          if (!isAuthorized) {
            toast({
              title: "Autorização pendente",
              description:
                type === PartnerType.PAN
                  ? "Ainda não encontramos a autorização do PAN. Conclua o fluxo no WhatsApp e tente novamente em instantes."
                  : "Ainda não encontramos a autorização do FGTS. Conclua no app do banco e tente novamente.",
              variant: "destructive",
            })
            return
          }
        } catch (error) {
          console.error(error)
        } finally {
          setIsLoading(false)
        }
      }
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent variant="bottomSheet" className="max-w-sm rounded-lg sm:rounded-xl" showCloseButton={false}>
        <div className="w-full">
          <div className="relative w-full">
            {!step?.image ? (
              <div
                className="mx-auto flex w-full max-w-[318px] items-center justify-center overflow-hidden rounded-2xl"
                style={{ aspectRatio: "318 / 319" }}
              >
                <Spinner show={true} size="small" />
              </div>
            ) : (
               !hasImageError ? (
                <div
                  className="mx-auto w-full max-w-[318px] overflow-hidden rounded-2xl bg-primary"
                  style={{ aspectRatio: "318 / 319" }}
                >
                  <img
                    key={currentStep}
                    src={step?.image}
                    alt={step?.title ?? ""}
                    className={`h-full w-full object-contain ${isImageLoading ? "opacity-0" : "opacity-100"}`}
                    loading="eager"
                    decoding="async"
                    onLoad={() => setIsImageLoading(false)}
                    onError={() => {
                      setHasImageError(true)
                      setIsImageLoading(false)
                    }}
                  />
                </div>
              ) : (
                <div
                  className="mx-auto w-full max-w-[318px] rounded-2xl bg-primary"
                  style={{ aspectRatio: "318 / 319" }}
                />
              )
            )}
          </div>
        </div>
        <div className="flex w-full flex-col items-center pt-4">
          <div className="mb-4 flex w-full items-center justify-center gap-2">
            {steps!.map((_, idx) => (
              <div
                key={idx}
                className={`h-2 flex-1 rounded-full ${idx <= currentStep ? "bg-primary" : "bg-gray01"}`}
              />
            ))}
          </div>
          <h3 className="mb-2 w-full text-left text-lg font-semibold text-black01 sm:text-xl">
            {step?.title}
          </h3>
          <div className="flex items-center gap-2 justify-between mb-5 w-full">
            <p className=" w-full text-left text-sm font-medium text-black01 sm:text-base">
              {formatDescription(step?.description ?? "")}
            </p>
            {stepLink && (
              <a href={step?.link ?? ''} target="_blank" rel="noopener noreferrer">
                <Button className="text-white rounded-full font-semibold h-12 flex-1 text-sm">
                  <Whatsapp
                    className="w-6 h-6"
                  />
                </Button>
              </a>
            )}
          </div>
          <div className="flex w-full gap-2 border-t border-gray02 pt-4">
            <button
              className="cursor-pointer flex-1 rounded-full bg-gray01 py-3 text-base font-bold text-black02 disabled:opacity-60"
              onClick={handlePrev}
            >
              Voltar
            </button>

            <Button
              isLoading={isLoading}
              onClick={handleNext}
              className="text-white rounded-full font-bold h-12 flex-1 text-md"
            >
              {currentStep === (steps?.length ?? 0) - 1 ? "Simular" : "Avançar"}
            </Button>

          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}